import React, { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useBackClose } from "../atoms/useBackClose"
import Pallino from "./Pallino"

// Pallino che accompagna (docs/sito.md). Ce n'è uno solo, per tutta la
// pagina: parte dall'illustrazione d'apertura, in campo; quando il campo
// esce dallo schermo salta giù, con un rimbalzo, e accompagna chi scorre;
// nelle sezioni le presenta con la sua battuta; in fondo salta nella scena
// di chiusura. Toccato apre l'indice, "Dove ti porto?", e ci si salta.
//
// Ha tre posti:
// - `home`, il suo posto nel campo d'apertura (`.scene-lead`), ed `end`,
//   quello nella scena di chiusura: lì sta *dentro* l'illustrazione (un
//   portale nel riquadro vuoto che la pagina gli lascia), e scorre con lei
//   come il resto del disegno, senza inseguirla;
// - a riposo, mentre accompagna: sul telefono l'angolo in basso a destra;
//   sul computer, se c'è margine, accanto al titolo della sezione dov'è
//   (`[data-guide-anchor]`). Qui è fisso sullo schermo.
// Da un posto all'altro fa un salto: un volo breve, con l'accelerazione
// all'inizio e la frenata alla fine e un arco verso l'alto, scritto a mano
// con `transform` a ogni fotogramma; atterrato, si schiaccia un attimo.
// Accanto ai titoli, da una sezione all'altra, cammina lungo il margine.
//
// `stops`: le fermate, in ordine, con l'`id` della sezione, il nome per
// l'indice (`nav`) e la battuta (`say`). `cta` disegna il bottone in fondo
// all'indice, e riceve come chiudere l'indice prima di aprire altro.

const BUBBLE_MS = 4200
const CORNER = 96
const SIDE = 92
const SIDE_GAP = 20
const FLIGHT_MS = 620
const ARC = 70

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
const clamp = (n, min, max) => Math.min(max, Math.max(min, n))
const lerp = (a, b, t) => a + (b - a) * t
const easeInOut = t => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2)
const boxOf = id => {
	const box = document.getElementById(id)?.getBoundingClientRect()
	return box?.width ? { x: box.left, y: box.top, size: box.width } : null
}

function GuideSheet({ stops, current, lead, cta, onPick, onClose }) {
	useBackClose(onClose)
	const firstRef = useRef(null)
	useEffect(() => {
		firstRef.current?.focus()
		const onKey = event => event.key === "Escape" && onClose()
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [onClose])

	return (
		<div className="guide-layer">
			<button type="button" className="guide-backdrop" aria-label="Chiudi l'indice" onClick={onClose} />
			<div className="guide-sheet" role="dialog" aria-modal="true" aria-labelledby="guide-title">
				{/* Pallino è salito sull'indice: toccato di nuovo, lo chiude, come
				    l'ha aperto */}
				<button type="button" className="guide-sheet-pallino" aria-label="Chiudi l'indice" onClick={onClose}>
					<Pallino kind={lead} pose="felice" size={64} />
				</button>
				<h2 id="guide-title">Dove ti porto?</h2>
				<p className="muted">Tocca e ci rimbalzo io.</p>
				<ul>
					{stops.map((stop, index) => (
						<li key={stop.id}>
							<button
								type="button"
								ref={index === 0 ? firstRef : undefined}
								className={stop.id === current ? "is-here" : undefined}
								aria-current={stop.id === current ? "location" : undefined}
								onClick={() => onPick(stop.id)}>
								{stop.nav}
								{stop.id === current && <span className="guide-here">Sei qui</span>}
							</button>
						</li>
					))}
				</ul>
				{cta(onClose)}
			</div>
		</div>
	)
}

// Dove sta il punto d'appoggio di una fermata: il suo posto accanto al
// titolo, se ce l'ha, se no la sezione
const anchorOf = id => {
	const section = document.getElementById(id)
	return section?.querySelector("[data-guide-anchor]") ?? section
}

export default function PallinoGuide({ stops, lead, cta, home, end, night }) {
	const [current, setCurrent] = useState(null)
	// home e end: dentro l'illustrazione; flying: in volo; rest: a riposo
	const [phase, setPhase] = useState("home")
	const [side, setSide] = useState("") // "", "left", "right"
	// a riposo nell'angolo, ancorato dal CSS: la classe la mette React, se no
	// la toglierebbe al primo ridisegno
	const [docked, setDocked] = useState(false)
	const [bubble, setBubble] = useState(null)
	const [open, setOpen] = useState(false)
	// il salto quando lo si manda altrove, lo schiacciarsi quando atterra
	const [motion, setMotion] = useState({ kind: "", n: 0 })
	const [slots, setSlots] = useState({ home: null, end: null })
	const greeted = useRef(false)
	const moverRef = useRef(null)
	const dockRef = useRef(null)
	const bubbleRef = useRef(null)
	const currentRef = useRef(null)
	currentRef.current = current
	// dov'è disegnato ora, per accostarsi piano al posto nuovo; e come
	// chiedere di ridisegnarlo
	const shownRef = useRef(null)
	const redrawRef = useRef(() => {})

	useEffect(() => {
		setSlots({ home: document.getElementById(home), end: document.getElementById(end) })
	}, [home, end])

	// La sezione dov'è: quella che passa a metà dello schermo
	const ids = stops.map(stop => stop.id).join(" ")
	useEffect(() => {
		if (!("IntersectionObserver" in window)) return
		const observer = new IntersectionObserver(
			entries => {
				const entering = entries.find(entry => entry.isIntersecting)
				if (entering) setCurrent(entering.target.id)
			},
			{ rootMargin: "-45% 0px -50% 0px" },
		)
		for (const id of ids.split(" ")) {
			const el = document.getElementById(id)
			if (el) observer.observe(el)
		}
		return () => observer.disconnect()
	}, [ids])

	// Dove sta, a ogni fotogramma in cui la pagina si muove o lui vola
	useEffect(() => {
		const mover = moverRef.current
		if (!mover) return
		let frame = 0
		let where = null // home, rest, end: dov'è o dove sta andando
		let flight = null // { from, start, at }
		let lastSide = null
		let lastDocked = null

		// a riposo: l'angolo, o accanto al titolo se c'è margine, nel margine
		// dalla parte del testo (a destra nelle sezioni col testo a destra);
		// il fumetto sta nel posto lasciato sopra il titolo
		const restOf = (vw, vh) => {
			// l'angolo lo tiene il CSS (`.guide-dock`): sui telefoni la barra del
			// browser, scorrendo, compare e sparisce, e un posto calcolato
			// dall'altezza dello schermo arriverebbe in ritardo, con uno scatto
			const dock = dockRef.current?.getBoundingClientRect()
			const corner = dock?.width
				? { x: dock.left, y: dock.top, size: dock.width }
				: { x: vw - CORNER - 16, y: vh - CORNER - 20, size: CORNER }
			const anchor = currentRef.current && anchorOf(currentRef.current)
			if (!anchor || vw < 1180) return { rest: corner, spot: null }
			const box = anchor.getBoundingClientRect()
			const onRight = Boolean(anchor.closest(".is-flipped"))
			const x = onRight ? box.right + SIDE_GAP : box.left - SIDE - SIDE_GAP
			if (x < 12 || x + SIDE > vw - 12) return { rest: corner, spot: null }
			return {
				rest: { x, y: clamp(box.top, 84, vh - SIDE - 120), size: SIDE },
				spot: { box, onRight },
			}
		}

		// dove deve stare: in campo finché l'apertura è quasi tutta sullo
		// schermo, nella chiusura quando la sua scena è entrata; un margine
		// tra andata e ritorno, così sul confine non fa avanti e indietro
		const wanted = vh => {
			const scene = document.getElementById(home)?.closest(".scene")?.getBoundingClientRect()
			const out = scene ? -scene.top / scene.height : 1
			if (out < (where === "home" ? 0.4 : 0.3)) return "home"
			const fin = document.getElementById(end)?.getBoundingClientRect()
			if (fin && (where === "end" ? fin.top < vh - 40 : fin.bottom < vh - 60)) return "end"
			return "rest"
		}

		const place = () => {
			frame = 0
			const vw = window.innerWidth
			const vh = window.innerHeight
			const calm = reducedMotion()
			const { rest, spot } = restOf(vw, vh)
			const targetOf = w => (w === "rest" ? rest : boxOf(w === "home" ? home : end) ?? rest)

			const bubbleAt = bubbleRef.current
			if (bubbleAt && spot) {
				bubbleAt.style.setProperty("--spot-left", `${spot.box.left.toFixed(1)}px`)
				bubbleAt.style.setProperty("--spot-right", `${(vw - spot.box.right).toFixed(1)}px`)
				// all'altezza di Pallino: se il titolo è già uscito dallo schermo,
				// il fumetto resta con lui e non se ne va col titolo
				bubbleAt.style.setProperty("--spot-top", `${rest.y.toFixed(1)}px`)
			}
			const sideNow = spot ? (spot.onRight ? "right" : "left") : ""
			if (sideNow !== lastSide) setSide((lastSide = sideNow))

			const want = wanted(vh)
			if (where === null) {
				// all'apertura della pagina è già dove deve stare
				where = want
				setPhase(want)
			} else if (want !== where) {
				const from = flight?.at ?? targetOf(where)
				where = want
				if (calm) {
					flight = null
					setPhase(want)
				} else {
					flight = { from, start: performance.now(), at: from }
					setPhase("flying")
				}
			}

			let at = null
			if (flight) {
				const t = clamp((performance.now() - flight.start) / FLIGHT_MS, 0, 1)
				const to = targetOf(where)
				const e = easeInOut(t)
				at = {
					x: lerp(flight.from.x, to.x, e),
					y: lerp(flight.from.y, to.y, e) - ARC * Math.sin(Math.PI * t),
					size: lerp(flight.from.size, to.size, e),
				}
				flight.at = at
				if (t >= 1) {
					flight = null
					setPhase(where)
					setMotion(m => ({ kind: "land", n: m.n + 1 }))
				} else frame = requestAnimationFrame(place)
			} else if (where === "rest" && !spot) {
				// nell'angolo lo tiene fermo il CSS (`.is-docked`, che vince sul
				// `transform` scritto qui): il posto scritto qui è lo stesso, e
				// serve solo finché React non ha messo la classe
				at = rest
			} else if (where === "rest") {
				// accanto ai titoli si cammina: da una sezione all'altra si
				// accosta piano al posto nuovo, invece di saltarci
				const shown = shownRef.current
				at = rest
				if (spot && shown && !calm) {
					at = {
						x: lerp(shown.x, rest.x, 0.2),
						y: lerp(shown.y, rest.y, 0.2),
						size: lerp(shown.size, rest.size, 0.2),
					}
					const settled = Math.abs(at.y - rest.y) < 0.5 && Math.abs(at.x - rest.x) < 0.5
					if (!settled) frame = requestAnimationFrame(place)
				}
			}
			const dockedNow = where === "rest" && !spot && !flight
			if (dockedNow !== lastDocked) setDocked((lastDocked = dockedNow))
			if (at) {
				shownRef.current = at
				mover.style.transform = `translate3d(${at.x.toFixed(1)}px, ${at.y.toFixed(1)}px, 0) scale(${(at.size / 120).toFixed(4)})`
			}
		}

		const schedule = () => {
			if (!frame) frame = requestAnimationFrame(place)
		}
		redrawRef.current = schedule
		place()
		window.addEventListener("scroll", schedule, { passive: true })
		window.addEventListener("resize", schedule)
		// le sezioni cambiano altezza (la scelta del circolo, un esempio che
		// si apre): si riguarda dove stanno
		const resize = "ResizeObserver" in window ? new ResizeObserver(schedule) : null
		resize?.observe(document.body)
		return () => {
			cancelAnimationFrame(frame)
			window.removeEventListener("scroll", schedule)
			window.removeEventListener("resize", schedule)
			resize?.disconnect()
		}
	}, [home, end])

	// cambiata sezione, il suo posto accanto al titolo è un altro
	// e chiuso l'indice, si rimette a posto senza aspettare che si scorra
	useEffect(() => redrawRef.current(), [current, open])

	// Entrando in una sezione, la presenta con la sua battuta. Sul telefono il
	// fumetto poi se ne va, perché copre la pagina; accanto ai titoli resta.
	// La prima volta dice anche che si tocca.
	useEffect(() => {
		if (phase !== "rest" || open || !current) {
			setBubble(null)
			return
		}
		const stop = stops.find(other => other.id === current)
		if (!stop?.say) return
		const hint = greeted.current ? "" : " Toccami per saltare altrove."
		greeted.current = true
		setBubble(stop.say + hint)
		if (side) return
		const timer = setTimeout(() => setBubble(null), BUBBLE_MS)
		return () => clearTimeout(timer)
		// le fermate cambiano con la scelta, la sezione dov'è no
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [current, phase, open, side])

	const close = useCallback(() => setOpen(false), [])

	const pick = id => {
		setOpen(false)
		if (!reducedMotion()) setMotion(m => ({ kind: "hop", n: m.n + 1 }))
		// dopo che il foglio ha lasciato la cronologia: il tasto indietro
		// del telefono lo chiude, e tornare indietro non deve riportare su
		setTimeout(() => {
			document.getElementById(id)?.scrollIntoView({
				behavior: reducedMotion() ? "auto" : "smooth",
				block: "start",
			})
		}, 60)
	}

	const parked = phase === "home" || phase === "end"
	const pose =
		phase === "end" ? (night ? "dorme" : "felice") : phase === "flying" || bubble ? "felice" : "fermo"
	const button = (
		<button
			type="button"
			key={motion.n}
			className={`guide-button${motion.kind ? ` is-${motion.kind}` : ""}${parked ? " is-parked" : ""}`}
			aria-label="Dove ti porto? L'indice del sito"
			onClick={() => {
				setBubble(null)
				setOpen(true)
			}}>
			<Pallino kind={lead} pose={pose} size={120} title={phase === "home" ? "Pallino" : undefined} />
		</button>
	)
	const slot = phase === "home" ? slots.home : phase === "end" ? slots.end : null

	return (
		<>
			{slot && createPortal(button, slot)}
			<div className="guide-dock" ref={dockRef} aria-hidden="true" />
			<div ref={moverRef} className={`guide guide-${phase}${docked ? " is-docked" : ""}${open ? " is-away" : ""}`} aria-hidden={parked}>
				{!parked && button}
			</div>
			<div
				ref={bubbleRef}
				className={`guide-bubble-at${side ? ` is-side is-${side}` : ""}`}
				aria-live="polite">
				{bubble && <p className="guide-bubble">{bubble}</p>}
			</div>
			{open && (
				<GuideSheet
					stops={stops}
					current={current}
					lead={lead}
					cta={cta}
					onPick={pick}
					onClose={close}
				/>
			)}
		</>
	)
}
